import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Star, ArrowLeft, RotateCcw, Clock } from "lucide-react";

interface PuzzlePiece {
  id: number;
  emoji: string;
  correctIndex: number;
  currentIndex: number;
}

interface PuzzleSet {
  name: string;
  emoji: string;
  pieces: string[];
}

const PUZZLES: PuzzleSet[] = [
  { name: "حيوانات المزرعة", emoji: "🐄", pieces: ["🐄", "🐔", "🐷", "🐴", "🐑", "🐐", "🦆", "🐇", "🐱"] },
  { name: "فواكه لذيذة", emoji: "🍎", pieces: ["🍎", "🍌", "🍇", "🍊", "🍓", "🍉", "🥝", "🍑", "🫐"] },
  { name: "الفضاء", emoji: "🚀", pieces: ["🚀", "🌙", "⭐", "🪐", "☄️", "🌍", "👨‍🚀", "🛸", "🌠"] },
  { name: "أشكال هندسية", emoji: "🔷", pieces: ["🔴", "🔵", "🟢", "🟡", "🟣", "🟠", "⬛", "🔶", "💠"] },
];

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

interface Props {
  onComplete: (score: number, stars: number, activityKey: string) => void;
  onBack: () => void;
}

export default function PuzzleActivity({ onComplete, onBack }: Props) {
  const [puzzleIdx, setPuzzleIdx] = useState<number | null>(null);
  const [pieces, setPieces] = useState<PuzzlePiece[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [moves, setMoves] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [finished, setFinished] = useState(false);
  const [result, setResult] = useState({ score: 0, stars: 0 });

  const startPuzzle = (idx: number) => {
    setPuzzleIdx(idx);
    const puzzle = PUZZLES[idx];
    const shuffled = shuffleArray(puzzle.pieces.map((emoji, i) => i));
    setPieces(puzzle.pieces.map((emoji, i) => ({
      id: i,
      emoji,
      correctIndex: i,
      currentIndex: shuffled[i],
    })));
    setSelectedId(null);
    setMoves(0);
    setStartTime(Date.now());
    setFinished(false);
  };

  const handlePieceClick = (clickedPiece: PuzzlePiece) => {
    if (selectedId === null) {
      setSelectedId(clickedPiece.id);
    } else {
      if (selectedId === clickedPiece.id) {
        setSelectedId(null);
        return;
      }

      setMoves(prev => prev + 1);
      setPieces(prev => {
        const updated = [...prev];
        const first = updated.find(p => p.id === selectedId)!;
        const second = updated.find(p => p.id === clickedPiece.id)!;
        const tempIdx = first.currentIndex;
        first.currentIndex = second.currentIndex;
        second.currentIndex = tempIdx;

        // Check if solved
        const isSolved = updated.every(p => p.currentIndex === p.correctIndex);
        if (isSolved) {
          const elapsed = (Date.now() - startTime) / 1000;
          const movePenalty = Math.max(0, (moves + 1 - 9) * 0.5);
          const timePenalty = Math.max(0, (elapsed - 30) * 0.1);
          const score = Math.max(1, Math.min(10, Math.round(10 - movePenalty - timePenalty)));
          const stars = score >= 8 ? 3 : score >= 5 ? 2 : 1;
          setResult({ score, stars });
          setFinished(true);
          onComplete(score, stars, "creative_puzzle");
        }

        return updated;
      });
      setSelectedId(null);
    }
  };

  // Sort pieces by their current position for display
  const displayPieces = [...pieces].sort((a, b) => a.currentIndex - b.currentIndex);

  if (finished) {
    return (
      <Card className="text-center border-2 border-amber-300 bg-gradient-to-b from-amber-50 to-white">
        <CardContent className="p-8 space-y-4">
          <div className="text-6xl">🧩</div>
          <h2 className="text-2xl font-bold text-foreground">أحسنت! أكملت الأحجية!</h2>
          <div className="flex justify-center gap-1">
            {[1, 2, 3].map(s => (
              <Star key={s} className={`h-10 w-10 ${s <= result.stars ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
            ))}
          </div>
          <p className="text-lg text-muted-foreground">أنهيت في {moves} حركة - {result.score} نقاط</p>
          <div className="flex gap-2 justify-center">
            <Button onClick={() => startPuzzle(puzzleIdx!)} variant="outline">
              <RotateCcw className="h-4 w-4 ml-1" /> إعادة
            </Button>
            <Button onClick={onBack} variant="outline">
              <ArrowLeft className="h-4 w-4 ml-2" /> العودة
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (puzzleIdx === null) {
    return (
      <div className="space-y-4">
        <Button onClick={onBack} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <Card className="border-2 border-amber-200">
          <CardHeader className="text-center">
            <div className="text-5xl mb-2">🧩</div>
            <CardTitle className="text-xl">اختر أحجية لتركيبها!</CardTitle>
            <p className="text-sm text-muted-foreground">رتّب القطع في مكانها الصحيح بالنقر على قطعتين لتبديلهما</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {PUZZLES.map((p, i) => (
                <button
                  key={i}
                  onClick={() => startPuzzle(i)}
                  className="p-4 rounded-xl border-2 border-muted hover:border-amber-400 bg-card hover:bg-amber-50 transition-all text-center"
                >
                  <div className="text-4xl mb-2">{p.emoji}</div>
                  <p className="font-medium text-foreground">{p.name}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button onClick={() => setPuzzleIdx(null)} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 ml-1" /> رجوع
        </Button>
        <Badge variant="secondary">🔄 {moves} حركة</Badge>
      </div>

      <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50">
        <CardHeader className="text-center pb-2">
          <CardTitle className="text-lg">رتّب الأشكال! اضغط على شكلين لتبديلهما 🧩</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
            {displayPieces.map(piece => {
              const isCorrect = piece.currentIndex === piece.correctIndex;
              const isSelected = selectedId === piece.id;
              return (
                <button
                  key={piece.id}
                  onClick={() => handlePieceClick(piece)}
                  className={`aspect-square rounded-xl text-3xl flex items-center justify-center border-2 transition-all ${
                    isSelected
                      ? "border-primary bg-primary/10 scale-110 shadow-lg"
                      : isCorrect
                      ? "border-green-400 bg-green-50"
                      : "border-muted bg-card hover:border-amber-400 hover:scale-105"
                  }`}
                >
                  {piece.emoji}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-center text-muted-foreground mt-3">
            💡 القطع الصحيحة تظهر بحدود خضراء
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
